import { useRef } from "react";

// The one write path onto a form state that has MORE THAN ONE WRITER, and the
// reason it goes through a ref rather than merging onto the render's `values`
// directly.
//
// A screen that mounts two forms over one value object — a site's own field
// beside its document shell, a page's beside its override — has two writers of
// that object on screen at once. Both write from an EFFECT: form-schema pushes
// a form's value up in `useEffect`, one commit behind the keystroke that caused
// it, so two of them can land in a single React batch with no render in
// between. Merging onto the `values` each handler was built in then means both
// merge onto the SAME pre-batch object, and the second call wins outright:
//
//     setValues({ ...values, title })    // values.config is pre-batch
//     setValues({ ...values, config })   // values.title  is pre-batch  <-- wins
//
// The lost half is gone with nothing on screen to say so, because FormInput
// holds the control's text itself and goes on showing what was typed.
//
// `state.setValues` cannot take an updater function — it extracts its argument
// against the target before storing it — so the accumulator is a ref advanced
// at WRITE time instead of at render time, which makes the writes commute in
// any order, batched or not. It is reassigned from `values` on every render so
// it re-syncs with whatever the store actually kept after extraction, and so a
// refetch is never fighting a stale copy.
//
// A hook rather than a helper, and its own module rather than an export of the
// first screen that needed it: the site's configuration screen and the page
// editor's Page tab have the same two-writer shape, and a copy of this in each
// is a copy of a concurrency fix nobody would think to keep in step. Both
// suites drive it directly, which is how two writes get put in one batch
// deliberately — left inline it would only ever be reachable through whatever
// interleaving jsdom happened to produce.
export const useWrite = (values, setValues) => {
  const latest = useRef(values);

  latest.current = values;

  return (mod) => {
    latest.current = { ...latest.current, ...mod };
    setValues(latest.current);
  };
};
